import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, X, Send, Zap } from 'lucide-react';
import { collection, doc, setDoc, updateDoc, getDocs, deleteDoc, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseInstance';
import { getAppId } from '../firebaseConfig';
import { callGeminiAPI } from '../utils/geminiAPI';
import { themes } from '../utils/themes';
import { getDefaultChatPrompts } from '../utils/profileRole';

const appId = getAppId();

export const ChatInterface = ({ profileData, onClose, onBack, profileOwnerId, user }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const messagesRef = useRef([]);
  const profileOwnerIdRef = useRef(profileOwnerId);
  const conversationIdRef = useRef(null);
  const isSavingRef = useRef(false); // Prevent concurrent saves
  const isCleaningUpRef = useRef(false); // Prevent concurrent cleanup operations
  
  // Lock body scroll when chat is open (prevents double-scroll glitch in Instagram in-app browser)
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []); // Run once on mount, cleanup on unmount
  
  // Safety check: ensure profileData exists (must be after hooks)
  if (!profileData) {
    return null;
  }
  
  const activeTheme = themes[profileData?.theme] || themes.midnight;

  // Keep refs in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    profileOwnerIdRef.current = profileOwnerId;
  }, [profileOwnerId]);

  // Security validation: In editor/preview mode, ensure profileOwnerId matches authenticated user
  useEffect(() => {
    if (onBack && user && profileOwnerId && profileOwnerId !== user.uid) {
      console.error('Security: profileOwnerId mismatch in editor/preview mode');
      // In preview/editor mode, profileOwnerId should always match authenticated user
      // This is a security check to prevent data leakage
    }
  }, [onBack, user, profileOwnerId]);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Auto-scroll to the latest message when new messages arrive. Gate on
  // messages.length > 0 — without this, the effect fires on initial mount
  // when messages = [] and scrolls the empty-state container down by ~80px.
  // The chat container has overflow: hidden, so the scrolled header (with
  // close button) ends up clipped above the visible area. Resizing the
  // window resets scrollTop and the header reappears, which is the
  // self-healing-on-resize symptom users were reporting. See QA design
  // audit 2026-04-25.
  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Cleanup function (disabled - keeping all conversations)
  // This function is kept for potential future use but is currently not called
  const cleanupOldConversations = async (profileOwnerId) => {
    // Prevent concurrent cleanup operations
    if (isCleaningUpRef.current) {
      return;
    }
    
    isCleaningUpRef.current = true;
    
    try {
      const conversationsRef = collection(db, 'artifacts', appId, 'users', profileOwnerId, 'conversations');
      // Fetch 11 conversations to check if we need cleanup (more efficient than fetching all)
      const q = query(conversationsRef, orderBy('createdAt', 'desc'), limit(11));
      const snapshot = await getDocs(q);
      
      if (snapshot.docs.length > 10) {
        // Delete oldest conversations (keep only the first 10, delete the rest)
        const toDelete = snapshot.docs.slice(10);
        await Promise.all(toDelete.map(docToDelete => deleteDoc(docToDelete.ref)));
        console.log(`Cleaned up ${toDelete.length} old conversation(s)`);
      }
    } catch (error) {
      console.error('Error cleaning up old conversations:', error);
    } finally {
      isCleaningUpRef.current = false;
    }
  };

  // Save conversation function - accepts messages directly to avoid stale closures
  const saveConversation = async (messagesToSave = null) => {
    // Always use the latest messages from ref if not provided, to ensure we save the most recent state
    const currentMessages = messagesToSave || messagesRef.current;
    const currentProfileOwnerId = profileOwnerIdRef.current;
    const currentConversationId = conversationIdRef.current;
    
    if (currentMessages.length === 0 || !currentProfileOwnerId) {
      console.warn('Cannot save: no messages or profileOwnerId', {
        messagesLength: currentMessages.length,
        hasProfileOwnerId: !!currentProfileOwnerId
      });
      return;
    }
    
    // Security: In editor/preview mode, verify profileOwnerId matches authenticated user
    // Note: This check is for editor/preview mode only. Public view chat is allowed.
    if (onBack && user && currentProfileOwnerId !== user.uid) {
      console.error('Security: Attempted to save conversation to different user');
      return; // Prevent saving to wrong user
    }
    
    // If a save is already in progress, wait for it to complete, then save with latest messages
    // This ensures we don't lose messages even if saves overlap
    if (isSavingRef.current) {
      console.log('Save already in progress, will save latest messages after current save completes');
      // Wait a bit and retry with latest messages
      setTimeout(() => {
        const latestMessages = messagesRef.current;
        if (latestMessages.length > 0) {
          saveConversation(latestMessages).catch(err => {
            console.error('Failed to save conversation (retry):', err);
          });
        }
      }, 500);
      return;
    }
    
    isSavingRef.current = true; // Set flag to prevent concurrent saves
    
    console.log('Saving conversation:', {
      messageCount: currentMessages.length,
      conversationId: currentConversationId || 'new',
      firstMessage: currentMessages[0]?.text?.substring(0, 50),
      lastMessage: currentMessages[currentMessages.length - 1]?.text?.substring(0, 50)
    });
    
    try {
      const conversationData = {
        messages: currentMessages,
        updatedAt: serverTimestamp(),
        profileOwnerId: currentProfileOwnerId,
        firstMessage: currentMessages[0]?.text || 'New conversation'
      };
      
      if (currentConversationId) {
        // Update existing conversation
        // Use updateDoc instead of setDoc to only update specified fields (preserves createdAt automatically)
        const existingDocRef = doc(db, 'artifacts', appId, 'users', currentProfileOwnerId, 'conversations', currentConversationId);
        
        // updateDoc only updates the fields we specify, so createdAt will be preserved automatically
        // This avoids needing read permission for visitors
        await updateDoc(
          existingDocRef,
          { 
            ...conversationData, 
            updatedAt: serverTimestamp()
            // Note: createdAt is automatically preserved since updateDoc only updates specified fields
          }
        );
      } else {
        // Create new conversation
        conversationData.createdAt = serverTimestamp();
        const convRef = doc(collection(db, 'artifacts', appId, 'users', currentProfileOwnerId, 'conversations'));
        await setDoc(convRef, conversationData);
        const newConversationId = convRef.id;
        setConversationId(newConversationId);
        conversationIdRef.current = newConversationId;
      }
      
      // Cleanup disabled - keeping all conversations
      // cleanupOldConversations(currentProfileOwnerId);
      
      console.log('Conversation saved successfully:', {
        conversationId: currentConversationId || 'new',
        messageCount: currentMessages.length
      });
      
    } catch (error) {
      console.error('Error saving conversation:', error);
      throw error; // Re-throw so caller can handle it
    } finally {
      isSavingRef.current = false; // Clear flag after save completes
    }
  };

  // Save conversation when chat closes
  const handleClose = () => {
    // Always clear any pending timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    // Always save the latest messages before closing
    // Use messagesRef.current to get the most up-to-date messages
    const latestMessages = messagesRef.current;
    if (latestMessages.length > 0 && profileOwnerIdRef.current) {
      console.log('Saving conversation on close:', {
        messageCount: latestMessages.length,
        conversationId: conversationIdRef.current || 'new'
      });
      // Save immediately (no timeout) when closing
      saveConversation(latestMessages).catch(err => {
        console.error('Failed to save conversation on close:', err);
      });
    }
    
    onClose();
  };

  // Cleanup on unmount (when chat closes via mobile button or other means)
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      // Reset refs to prevent stale references after unmount
      isSavingRef.current = false;
      isCleaningUpRef.current = false;
    };
  }, []); // Only run on unmount

  // Internal function that does the actual sending
  const sendMessage = async (textToSend) => {
    const trimmedText = String(textToSend).trim();
    if (!trimmedText) return;
    
    // Validate and clean current messages before adding new one
    const currentMessages = (messagesRef.current || []).filter(msg => 
      msg && 
      typeof msg === 'object' && 
      msg.text && 
      typeof msg.text === 'string' && 
      msg.role && 
      (msg.role === 'user' || msg.role === 'ai')
    );
    
    const userMsg = { 
      id: Date.now(), 
      role: 'user', 
      text: trimmedText 
    };
    
    // Build the updated messages array with the new user message
    const updatedMessages = [...currentMessages, userMsg];
    
    // Batch state updates to avoid multiple re-renders
    // React 18+ automatically batches these, but we'll use startTransition for better UX
    setMessages(updatedMessages);
    setIsTyping(true); // Show typing indicator
    
    let finalMessages = null;
    
    try {
      // Use updated messages for API call (includes full conversation history)
      const context = updatedMessages.length === 0 ? [{role: 'user', text: 'Hello'}] : updatedMessages;
      const res = await callGeminiAPI(context, profileData, userMsg.text);
      
      // Validate AI response
      let aiResponseText = res && typeof res === 'string' ? res.trim() : null;
      
      // Check if response is an error message
      const isErrorResponse = !aiResponseText || 
        aiResponseText.includes('buffering') || 
        aiResponseText.includes('trouble processing') ||
        aiResponseText.includes('trouble connecting') ||
        aiResponseText.includes('too many requests') ||
        aiResponseText.includes('authentication issues');
      
      // Prevent duplicate error messages
      if (isErrorResponse) {
        // Look through messages in reverse to find the last AI message
        const lastAIMessage = [...updatedMessages].reverse().find(msg => msg?.role === 'ai');
        const wasLastMessageAnError = lastAIMessage && 
          (lastAIMessage.text?.includes('buffering') || 
           lastAIMessage.text?.includes('trouble') ||
           lastAIMessage.text?.includes('try again') ||
           lastAIMessage.text?.includes('too many requests') ||
           lastAIMessage.text?.includes('authentication issues') ||
           lastAIMessage.text?.includes('trouble connecting'));
        
        if (wasLastMessageAnError) {
          // Skip adding duplicate error - don't add any response
          console.log('Skipped duplicate error message from API');
          finalMessages = updatedMessages; // Just keep the user message
          setMessages(updatedMessages);
          messagesRef.current = updatedMessages;
          setIsTyping(false);
          // Still save the conversation with just the user message
          if (updatedMessages.length > 0 && profileOwnerIdRef.current) {
            if (saveTimeoutRef.current) {
              clearTimeout(saveTimeoutRef.current);
              saveTimeoutRef.current = null;
            }
            saveTimeoutRef.current = setTimeout(() => {
              saveConversation(updatedMessages).catch(err => {
                console.error('Failed to save conversation:', err);
              });
              saveTimeoutRef.current = null;
            }, 500);
          }
          return; // Exit early, don't add error response
        } else {
          // First error, use the error message from API or fallback
          aiResponseText = aiResponseText || "I'm having trouble processing that. Could you try again?";
        }
      }
      
      const aiResponse = { 
        id: Date.now() + 1, 
        role: 'ai', 
        text: aiResponseText 
      };
      
      // Calculate the final messages array with AI response
      finalMessages = [...updatedMessages, aiResponse];
      
      // Update state and ref
      setMessages(finalMessages);
      messagesRef.current = finalMessages;
      setIsTyping(false);
      
    } catch (error) {
      console.error('Error in handleSend:', error);
      
      // Check if the last AI message was already an error message to prevent duplicates
      // Look through messages in reverse to find the last AI message
      const lastAIMessage = [...updatedMessages].reverse().find(msg => msg?.role === 'ai');
      const wasLastMessageAnError = lastAIMessage && 
        (lastAIMessage.text?.includes('buffering') || 
         lastAIMessage.text?.includes('trouble') ||
         lastAIMessage.text?.includes('try again') ||
         lastAIMessage.text?.includes('too many requests') ||
         lastAIMessage.text?.includes('authentication issues') ||
         lastAIMessage.text?.includes('trouble connecting'));
      
      // Even on error, save the user message and show error response
      // This ensures user messages are never lost
      // But don't add duplicate error messages if the last AI response was also an error
      if (!wasLastMessageAnError) {
        const errorResponse = { 
          id: Date.now() + 1, 
          role: 'ai', 
          text: "I'm having trouble processing that right now. Could you try again in a moment? 🧠✨"
        };
        finalMessages = [...updatedMessages, errorResponse];
        
        // Update state and ref
        setMessages(finalMessages);
        messagesRef.current = finalMessages;
      } else {
        // If last message was already an error, just keep the user message without adding another error
        finalMessages = updatedMessages;
        setMessages(updatedMessages);
        messagesRef.current = updatedMessages;
        console.log('Skipped duplicate error message');
      }
      
      setIsTyping(false);
    }
    
    // ALWAYS save conversation, even if API failed
    // This ensures all messages are saved to DB
    if (finalMessages && finalMessages.length > 0 && profileOwnerIdRef.current) {
      // Clear any existing timeout to prevent saving stale data
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      
      // Save with debounce, but ensure we always save the latest messages
      // Use a closure to capture the current finalMessages
      const messagesToSave = finalMessages;
      saveTimeoutRef.current = setTimeout(() => {
        // Double-check we're saving the latest messages
        const latestMessages = messagesRef.current;
        // Use the latest messages if they're more recent (longer array)
        const messagesToSaveFinal = latestMessages.length >= messagesToSave.length ? latestMessages : messagesToSave;
        
        saveConversation(messagesToSaveFinal).catch(err => {
          console.error('Failed to save conversation:', err);
        });
        saveTimeoutRef.current = null;
      }, 500); // Increased from 200ms to 500ms to reduce save frequency but ensure all messages are captured
    }
  };

  // Public handleSend - uses input state directly
  const handleSend = async () => {
    const textToSend = input.trim();
    if (!textToSend) return;
    setInput(''); // Clear input immediately
    await sendMessage(textToSend);
  };

  // Helper for prompt buttons
  const handleSendPrompt = async (promptText) => {
    const textToSend = String(promptText).trim();
    if (!textToSend) return;
    await sendMessage(textToSend);
  };

  return (
    <div 
      className={`flex flex-col w-full max-w-2xl rounded-none sm:rounded-[2.5rem] border-0 sm:border border-white/20 animate-in zoom-in duration-300 relative font-sans ${activeTheme.type === 'dark' ? 'bg-[#0f0f12] text-white' : 'bg-white text-gray-900'}`}
      style={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div className={`absolute -top-20 -left-20 w-96 h-96 rounded-full blur-[100px] opacity-40 bg-gradient-to-br ${activeTheme.accent} hidden sm:block`}></div>
      <div className={`absolute -bottom-20 -right-20 w-96 h-96 rounded-full blur-[100px] opacity-40 bg-gradient-to-tl ${activeTheme.accent} hidden sm:block`}></div>

      <div className={`relative z-[60] flex items-center justify-between p-3 sm:p-4 ${activeTheme.type === 'dark' ? 'bg-[#0f0f12]' : 'bg-white'} border-b ${activeTheme.type === 'dark' ? 'border-white/10' : 'border-gray-200'} shadow-lg overflow-visible`} style={{ flexShrink: 0 }}>
        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1 overflow-hidden">
          {onBack && (
            <button 
              onClick={onBack} 
              className={`hidden sm:flex mr-2 p-2.5 sm:p-3 rounded-full transition-all shrink-0 z-40 ${activeTheme.type === 'dark' ? 'bg-white/15 hover:bg-white/25 text-white border-2 border-white/30' : 'bg-gray-200 hover:bg-gray-300 text-gray-800 border-2 border-gray-300'} shadow-lg`}
              aria-label="Go back"
              title="Go back"
            >
              <ArrowLeft size={22} className="sm:w-6 sm:h-6" strokeWidth={2.5} />
            </button>
          )}
          <div className="relative shrink-0">
            {profileData.avatar && profileData.avatar.trim() ? (
              <img 
                src={profileData.avatar} 
                alt="Avatar" 
                className="w-9 h-9 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-white/30 shadow-lg block"
                onError={(e) => {
                  // Hide image on error instead of showing default
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/20 border-2 border-white/30 shadow-lg flex items-center justify-center">
                <span className="text-white text-xs sm:text-sm font-medium">
                  {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'A'}
                </span>
              </div>
            )}
            <div className="absolute bottom-0 right-0 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-green-400 rounded-full border-2 border-black"></div>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-xs sm:text-sm tracking-tight truncate">{profileData.name} <span className="opacity-50 text-[9px] sm:text-[10px] uppercase">AI</span></h3>
            <p className="text-[9px] sm:text-[10px] font-medium opacity-60 flex items-center"><Zap size={9} className="sm:w-[10px] sm:h-[10px] mr-1 fill-current shrink-0"/> Always Active</p>
          </div>
        </div>
        <button 
          onClick={handleClose} 
          className={`flex ml-2 p-2 sm:p-2.5 rounded-full transition-all shrink-0 z-[60] relative bg-red-500 hover:bg-red-600 active:bg-red-700 text-white border-2 border-white shadow-2xl`}
          aria-label="Close chat"
          title="Close"
          style={{ 
            minWidth: '40px', 
            minHeight: '40px',
            flexShrink: 0
          }}
        >
          <X size={18} className="font-bold" strokeWidth={3} />
        </button>
      </div>

      <div
        ref={messagesContainerRef}
        className="relative z-10 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-4 sm:py-2 scrollbar-hide flex flex-col"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          WebkitOverflowScrolling: 'touch',
          overflowAnchor: 'none'
        }}
      >
        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center space-y-5 sm:space-y-6 text-center py-8 sm:py-10 px-4 sm:px-6">
            <div className="relative group z-10 shrink-0">
              <div className={`absolute inset-0 bg-gradient-to-r ${activeTheme.accent} blur-xl opacity-30 group-hover:opacity-50 transition-opacity`}></div>
              {profileData.avatar && profileData.avatar.trim() ? (
                <img 
                  src={profileData.avatar} 
                  className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover shadow-xl border-2 border-white/20 z-10 block"
                  onError={(e) => {
                    // Hide image on error instead of showing default
                    e.target.style.display = 'none';
                  }}
                  alt="Profile"
                />
              ) : (
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white/20 shadow-xl border-2 border-white/20 z-10 flex items-center justify-center">
                  <span className="text-white text-2xl sm:text-3xl font-medium">
                    {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'A'}
                  </span>
                </div>
              )}
            </div>
            <div className="space-y-2 shrink-0">
              <p className={`relative text-lg sm:text-xl font-bold tracking-tight px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r ${activeTheme.accent} bg-clip-text text-transparent drop-shadow-lg`} style={{
                textShadow: '0 0 20px rgba(99, 102, 241, 0.3)',
                filter: 'drop-shadow(0 0 8px rgba(99, 102, 241, 0.4))'
              }}>
                {profileData.chatIntroText?.replace('[Name]', profileData.name.split(' ')[0] || 'Your Name') || 'Trained by ' + (profileData.name.split(' ')[0] || 'Your Name') + ' • Same vibe, different dimension ✨'}
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3 w-full max-w-md px-2 shrink-0">
              {(profileData.chatPrompts || getDefaultChatPrompts(profileData)).map((s, i) => (
                <button 
                  key={i} 
                  onClick={() => handleSendPrompt(s)} 
                  className={`px-4 sm:px-5 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-bold border transition-all hover:scale-105 active:scale-95 shrink-0 ${activeTheme.type === 'dark' ? 'bg-white/5 border-white/10 hover:bg-white/10' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-end space-y-4 sm:space-y-6">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2`}>
                <div className={`max-w-[85%] sm:max-w-[80%] px-4 sm:px-5 py-2.5 sm:py-3 text-sm sm:text-base font-medium leading-relaxed rounded-xl sm:rounded-2xl shadow-sm backdrop-blur-sm break-words ${msg.role === 'user' ? `text-white bg-gradient-to-br ${activeTheme.accent}` : `${activeTheme.cardBg} border border-white/10`}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isTyping && <div className="text-xs sm:text-sm opacity-40 animate-pulse pl-2">typing...</div>}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div 
        className={`relative z-10`} 
        style={{ 
          flexShrink: 0,
          flexGrow: 0,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        <div className={`p-4 sm:p-5 border-t ${activeTheme.type === 'dark' ? 'border-white/10' : 'border-gray-200'}`}>
          <div className={`flex items-center p-2 sm:p-2.5 rounded-2xl sm:rounded-[2rem] border transition-all ${activeTheme.type === 'dark' ? 'bg-white/5 border-white/10 focus-within:border-white/30' : 'bg-gray-50 border-gray-200 focus-within:border-gray-400'}`}>
            <input 
              type="text" 
              value={input} 
              onChange={(e) => setInput(e.target.value)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSend();
                }
              }} 
              placeholder="Type a message..." 
              className="flex-1 bg-transparent px-3 sm:px-4 py-2 sm:py-2.5 text-base sm:text-base focus:outline-none placeholder:opacity-50" 
              autoFocus 
            />
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSend();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSend();
              }}
              disabled={!input.trim()} 
              className={`p-2.5 sm:p-3 rounded-full transition-all shrink-0 ${input.trim() ? 'opacity-100' : 'opacity-50 cursor-not-allowed'} ${activeTheme.buttonBg}`}
              aria-label="Send message"
            >
              <Send size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
        <p className="text-[9px] text-center pb-1.5 opacity-20 font-medium tracking-widest uppercase">Powered by PerAI</p>
      </div>
    </div>
  );
};

